var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { once } from 'lodash';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { StateMachine } from '@sierralabs/state-machine';
import { useTrackChanges } from '@sierralabs/track-changes-react';
import { useAccumulate } from './Form.hooks';
import FormDragProvider from './FormDragContext';
import { useEditFormElement } from './FormElement.hooks';
import { useEditFormSection } from './FormSection.hooks';
import { defaultTransitionConditionPlugins } from './plugins/transition-conditions/default';
import { defaultActionPlugins } from './plugins/actions/default';
import { defaultFormElementPlugins } from './plugins/form-elements/default';
const createFormContext = once(() => createContext(null));
export const useFormContext = () => {
    const context = useContext(createFormContext());
    if (!context) {
        throw new Error('useFormContext must be used within a FormProvider');
    }
    return context;
};
/**
 * The Form Context provides access to entity schema, form state, form values,
 * form edit state, and events when form is edited and changed. It is not aware
 * of form schema and form template entities to remain unopinionated about the
 * data source. See `FormDataSource` component on built in form data storage.
 */
export const FormProvider = (_a) => {
    var { entitySchema, formStateNode, values, children, isEditSaving, disableDataSources, onEditSchema, onEdit, onChange, onFieldChange } = _a, props = __rest(_a, ["entitySchema", "formStateNode", "values", "children", "isEditSaving", "disableDataSources", "onEditSchema", "onEdit", "onChange", "onFieldChange"]);
    if (formStateNode && !formStateNode.states) {
        formStateNode.states = [];
    }
    const getDataSourcePropsRef = useRef(props.getDataSourceProps);
    getDataSourcePropsRef.current = props.getDataSourceProps;
    const onEditSchemaRef = useRef(onEditSchema);
    const onEditRef = useRef(onEdit);
    const onFieldChangeRef = useRef(onFieldChange);
    // setup track changes for form template changes
    const trackChanges = useTrackChanges(formStateNode || {});
    // setup state machine for handling form section transitions
    const elementPluginsRef = useRef(props.elementPlugins || defaultFormElementPlugins);
    const actionPluginsRef = useRef(props.actionPlugins || defaultActionPlugins);
    const transitionConditionPluginsRef = useRef(props.transitionConditionPlugins || defaultTransitionConditionPlugins);
    const additionalSectionConfigurationsRef = useRef(props.additionalSectionConfigurations || []);
    const additionalEventConfigurationsRef = useRef(props.additionalEventConfigurations || []);
    const transitionButtonRef = useRef(props.transitionButton);
    const machineOptions = useMemo(() => {
        const options = { actions: {}, guards: {} };
        for (const plugin of actionPluginsRef.current) {
            options.actions[plugin.type] = plugin.action;
        }
        for (const plugin of transitionConditionPluginsRef.current) {
            options.guards[plugin.type] = plugin.guard;
        }
        return options;
    }, []);
    const stateMachineRef = useRef(formStateNode
        ? new StateMachine(formStateNode, machineOptions)
        : undefined);
    // setup form state
    const [formState, setFormState] = useState({
        lastFormElementType: 'TextField',
        isSubmitting: false
    });
    const [formValues, setFormValues] = useState(values || {});
    const previousFormValuesRef = useRef(formValues);
    // changedFormValue is used by the data source to determine form value changes
    const [changedFormValue, setChangedFormValue] = useAccumulate();
    const runActions = useCallback((key, onUpdateOnly = false) => __awaiter(void 0, void 0, void 0, function* () {
        var _b, _c;
        const state = (_b = stateMachineRef.current) === null || _b === void 0 ? void 0 : _b.currentState;
        if (!(state === null || state === void 0 ? void 0 : state.context)) {
            return;
        }
        console.debug('[FormContext] runActions');
        const runAction = (action) => __awaiter(void 0, void 0, void 0, function* () {
            const plugin = actionPluginsRef.current.find(a => a.type === action.type);
            // NOTE: only run action plugins with runOnUpdate flag
            if (!plugin ||
                !action.params ||
                (onUpdateOnly && !plugin.runOnUpdate)) {
                return;
            }
            try {
                yield plugin.action(state, action.params);
            }
            catch (error) {
                console.error(error);
            }
        });
        // if key is available, find which sections the key is from
        // otherwise, use all sections to run actions
        const sections = state.context.formSections || [];
        const filteredSections = !key
            ? sections
            : sections.filter(section => {
                var _a, _b;
                return (_b = (_a = section.meta) === null || _a === void 0 ? void 0 : _a.elements) === null || _b === void 0 ? void 0 : _b.some(element => {
                    return (element.key === key ||
                        (element.dataSource && element.dataSource === key));
                });
            });
        const promises = [];
        for (const section of filteredSections) {
            // run section actions
            for (const action of section.onEntry || []) {
                promises.push(runAction(action));
            }
            // run element actions
            for (const element of ((_c = section.meta) === null || _c === void 0 ? void 0 : _c.elements) || []) {
                for (const action of element.actions || []) {
                    promises.push(runAction(action));
                }
            }
        }
        yield Promise.all(promises);
    }), []);
    // used by the form elements to set form value changes
    const setFormValue = useCallback((key, value, element, skipActions = false) => {
        var _a, _b;
        // notify data sources that a form value has changed
        setChangedFormValue({ [key]: value });
        // set form value
        if (Array.isArray(formValues[key])) {
            // capture previous form value array for use in assign action
            previousFormValuesRef.current[key] = [
                ...formValues[key]
            ];
        }
        setFormValues(Object.assign(Object.assign({}, formValues), { [key]: value }));
        (_a = onFieldChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onFieldChangeRef, key, value);
        // run element action on form value change
        const state = (_b = stateMachineRef.current) === null || _b === void 0 ? void 0 : _b.currentState;
        if (state === null || state === void 0 ? void 0 : state.context) {
            state.context.formValues[key] = value;
            !skipActions && runActions(key, true);
        }
    }, [formValues, runActions, setChangedFormValue]);
    const clearFormValues = useCallback((keys) => {
        if (keys && keys.length > 0) {
            setFormValues(formValues => {
                keys.forEach(key => {
                    delete formValues[key];
                });
                return Object.assign({}, formValues);
            });
        }
        else {
            setFormValues({});
        }
    }, []);
    /**
     * Add a form schema column via track changes delta
     */
    // const addFormSchemaColumn = useCallback(
    //   (column: ColumnSchema) => {
    //     if (!entitySchema) {
    //       console.error('No Entity Schema defined.');
    //       return;
    //     }
    //     const trackChanges = new TrackChanges(entitySchema);
    //     trackChanges.insert('columns', column);
    //     onEditSchemaRef.current?.(trackChanges);
    //   },
    //   [entitySchema]
    // );
    /**
     * send onEdit event when form is modified
     */
    const notifyFormEdit = useCallback(() => {
        var _a;
        // re-initialize the state machine when form template changes
        if (trackChanges.final) {
            stateMachineRef.current = new StateMachine(trackChanges.final, machineOptions);
            (_a = onEditRef.current) === null || _a === void 0 ? void 0 : _a.call(onEditRef, trackChanges);
        }
    }, [machineOptions, trackChanges]);
    const editFormSectionProps = useEditFormSection(trackChanges, formState, notifyFormEdit);
    const editFormElementProps = useEditFormElement(trackChanges, formState, notifyFormEdit);
    const FormContext = createFormContext();
    const formContext = useMemo(() => (Object.assign(Object.assign(Object.assign({ entitySchema,
        formState, formSections: trackChanges.final.states || [], formValues, previousFormValues: previousFormValuesRef.current, changedFormValue,
        trackChanges, stateMachine: stateMachineRef.current, disableDataSources,
        setFormState,
        setFormValues,
        setFormValue,
        clearFormValues, getDataSourceProps: getDataSourcePropsRef.current }, editFormSectionProps), editFormElementProps), { notifyFormEdit, elementPlugins: elementPluginsRef.current, actionPlugins: actionPluginsRef.current, transitionConditionPlugins: transitionConditionPluginsRef.current, additionalSectionConfigurations: additionalSectionConfigurationsRef.current, additionalEventConfigurations: additionalEventConfigurationsRef.current, transitionButton: transitionButtonRef.current })), [
        entitySchema,
        disableDataSources,
        changedFormValue,
        clearFormValues,
        editFormElementProps,
        editFormSectionProps,
        formState,
        formValues,
        notifyFormEdit,
        setFormValue,
        trackChanges
    ]);
    // handle form value changes and event propagation
    useEffect(() => {
        var _a;
        if (formValues && formValues !== previousFormValuesRef.current) {
            previousFormValuesRef.current = formValues;
            // update state machine context values
            if (((_a = stateMachineRef.current) === null || _a === void 0 ? void 0 : _a.isStarted) &&
                !stateMachineRef.current.isTransitioning) {
                // resume the state machine to check for any auto transition guards
                stateMachineRef.current.resumeContext(formContext);
            }
            onChange === null || onChange === void 0 ? void 0 : onChange(formValues);
        }
    }, [formContext, formValues, onChange]);
    return (React.createElement(FormContext.Provider, { value: formContext },
        React.createElement(FormDragProvider, null, children)));
};
export default FormProvider;
//# sourceMappingURL=FormContext.js.map